import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { onTourImageError } from "@/lib/imageFallback";
import { listEvents, formatEventDate, type EventItem } from "@/lib/events";

export default function EventosPage() {
  const { t } = useTranslation("events");
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = t("events.pageTitle");
    let cancelled = false;
    listEvents()
      .then((list) => { if (!cancelled) setEvents(list); })
      .catch(() => { if (!cancelled) setEvents([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-offwhite pt-16 md:pt-20">
      <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-16">
        <div className="max-w-6xl mx-auto">
          <h1 className="font-display text-3xl md:text-4xl font-bold text-charcoal mb-2">{t("events.listingTitle")}</h1>
          <p className="text-gray-500 mb-10">{t("events.listingSubtitle")}</p>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
                  <div className="h-44 bg-gray-200" />
                  <div className="p-4 space-y-2"><div className="h-3 bg-gray-200 rounded w-1/3" /><div className="h-4 bg-gray-200 rounded w-2/3" /></div>
                </div>
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 text-gray-300">
                <i className="ri-calendar-event-line text-4xl" />
              </div>
              <p className="text-gray-500">{t("events.emptyListing")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((ev) => (
                <Link
                  key={ev.id}
                  to={`/eventos/${ev.slug}`}
                  className="group flex flex-col bg-white rounded-2xl overflow-hidden border border-gray-100 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="h-44 overflow-hidden">
                    <img
                      src={ev.imageUrl ?? "/brand/hero/i4-1200.jpg"}
                      alt={ev.title}
                      onError={onTourImageError}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <p className="text-xs text-coral font-semibold mb-1.5 flex items-center gap-1.5">
                      <i className="ri-calendar-event-line" /> {formatEventDate(ev)}
                    </p>
                    <h3 className="font-semibold text-charcoal leading-snug">{ev.title}</h3>
                    {ev.shortDescription && <p className="text-sm text-gray-500 mt-2 line-clamp-2">{ev.shortDescription}</p>}
                    {(ev.location || ev.city) && (
                      <p className="text-xs text-gray-400 mt-auto pt-3 flex items-center gap-1">
                        <i className="ri-map-pin-line" /> {[ev.location, ev.city].filter(Boolean).join(", ")}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
